import { Divider } from '@magicschool/spellbook/components/Divider';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { StepperStepProps } from '@magicschool/spellbook/components/Stepper/Stepper';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';

const stepperDividerVariants = cva('z-0 absolute transform top-4 ltr:left-0  ltr:translate-x-1/2 rtl:right-0 rtl:-translate-x-1/2', {
  variants: {
    isCompleted: {
      true: 'bg-primary-stroke',
    },
  },
});

export const StepperStepSlot = Slot<StepperStepProps>({
  displayName: 'Stepper.Step',
  renderDefault: ({ children, className, isLast, isCompleted, isCurrent, ...props }) => {
    const { slots } = useSlots(children, {
      label: { Component: StepperStepLabelSlot, props: { children, isCurrent } },
    });

    return (
      <li
        data-spellbook-name="stepper-step"
        className={cn('flex-1 min-w-32 text-pretty whitespace-normal overflow-wrap z-1 flex flex-col', className)}
        {...(isCurrent ? { 'aria-current': 'step' } : {})}
        {...props}
      >
        <div className={cn('flex items-center flex-col relative')}>
          {!isLast && <Divider orientation="horizontal" decorative className={cn(stepperDividerVariants({ isCompleted }))} />}
          <div className="p-2 z-1">
            <Icon
              name={isCompleted ? 'circle-check' : 'circle'}
              color={isCompleted || isCurrent ? 'primary' : 'disabled'}
              className="fill-surface-background-0 rtl:scale-x-[-1]"
            />
          </div>
          {slots.label}
        </div>
      </li>
    );
  },
});

export const StepperStepLabelSlot = Slot<TextProps & { isCurrent?: boolean }>({
  displayName: 'Stepper.StepLabel',
  isRenderedByDefault: true,
  renderDefault: ({ children, className, isCurrent, ...props }) => {
    return (
      <Text
        data-spellbook-name="stepper-step-label"
        variant={isCurrent ? 'label-2' : 'label-1'}
        color={isCurrent ? 'default-strong' : 'default-soft'}
        className={cn('text-center', className)}
        {...props}
      >
        {children}
      </Text>
    );
  },
});
